import Button from '@mortgage-pos/ui/components/Button'
import { useStoreActions } from '@mortgage-pos/ui/store'
import { iconMap } from '@mortgage-pos/ui/utils/customizationHelpers'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { supportedFileFormats } from '@mortgage-pos/ui/data/supportedFileFormats'

interface FileUploadProps {
  fileUploadHandler: (file: File) => void
  uploadButtonText?: string
  manualAddressClickHandler: () => void
}

const FileUpload: FC<FileUploadProps> = ({
  fileUploadHandler,
  uploadButtonText,
  manualAddressClickHandler,
}) => {
  const [isDragOverSuccess, setIsDragOverSuccess] = useState(false)
  const [dragLevel, setDragLevel] = useState(0)

  const { elementClicked } = useStoreActions((store) => store.tracking)

  const fileInputField = useRef(null)
  const dragAndDropDivField = useRef(null)

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.currentTarget.files)
    fileUploadHandler(newFiles[0])
  }

  const onUploadClick = () => fileInputField.current.click()

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const { items } = e.dataTransfer
    if (!items) return

    const files = Array.from(items)
    const hasInvalidFile = files.reduce(
      (result, file) =>
        result || file.kind !== 'file' || !file.type.includes('pdf'),
      false
    )

    setDragLevel((p) => p + 1)
    setIsDragOverSuccess(!hasInvalidFile)
  }

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragLevel((p) => p - 1)
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const onDragDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const { files: rawFiles } = e.dataTransfer
    const files = rawFiles ? Array.from(rawFiles) : []

    const validFileType = supportedFileFormats.some((file) => {
      return files[0].type.includes(file)
    })

    const hasInvalidFile = files.length !== 1 || !validFileType

    if (hasInvalidFile) {
      setDragLevel(0)
      return
    }

    fileUploadHandler(files[0])
  }

  useEffect(() => {
    const div = dragAndDropDivField.current
    if (!div) return
    div.addEventListener('dragenter', onDragEnter)
    div.addEventListener('dragleave', onDragLeave)
    div.addEventListener('dragover', onDragOver)
    div.addEventListener('drop', onDragDrop)
    return () => {
      div.removeEventListener('dragenter', onDragEnter)
      div.removeEventListener('dragleave', onDragLeave)
      div.removeEventListener('dragover', onDragOver)
      div.removeEventListener('drop', onDragDrop)
    }
  }, [dragAndDropDivField])

  const fileUploadClasses = useMemo(() => {
    const baseClass = 'br-file-upload__cta'
    if (!dragLevel) return baseClass

    if (isDragOverSuccess) {
      return `${baseClass} success`
    }

    return `${baseClass} failure`
  }, [dragLevel, isDragOverSuccess])

  const handleManualAddressClicked = () => {
    elementClicked({
      elementType: 'Button',
      location: 'Compare Upload page',
      actionOutcome: 'User clicked to manually enter address',
      text: 'Enter my address',
    })

    manualAddressClickHandler()
  }

  return (
    <>
      <section className="br-file-upload__container --mobile">
        <div className="br-file-upload__file-drop-container">
          <div className={fileUploadClasses}>
            <p className="br-file-upload__cta-title">Get Started</p>
            <p className="br-file-upload__cta-body">
              Take a photo of your mortgage statement or enter your address
            </p>
            <p className="br-file-upload__cta-subtitle">
              We'll use your current mortgage info to find your custom offers
            </p>

            <Button
              className="br-file-upload__cta-button"
              content={'Take photo or upload'}
              onClick={onUploadClick}
            />
            <span className="br-file-upload__file-types">
              (JPG, PDF, or PNG)
            </span>
            <p className="br-file-upload__or-separator">OR</p>
            <Button
              className="br-file-upload__cta-button-inverted"
              content="Enter my address"
              onClick={handleManualAddressClicked}
            />
            <input
              type="file"
              ref={fileInputField}
              onChange={handleFileUpload}
              pattern="^.+\.pdf$"
              title=""
              value=""
            />
          </div>
        </div>
      </section>
      <section className="br-file-upload__container --desktop">
        <div className="br-file-upload__file-drop-container">
          <div
            className={fileUploadClasses}
            onClick={onUploadClick}
            ref={dragAndDropDivField}
          >
            <img
              src={iconMap('uploadCloud')}
              className="br-file-upload__cta-image"
              alt="upload icon"
            />
            <p className="br-file-upload__cta-body">
              Drag & Drop your statement
            </p>
            <Button
              className="br-file-upload__cta-button"
              content={'Upload File'}
            />
            <span className="br-file-upload__file-types">
              (JPG, PDF, or PNG)
            </span>
          </div>
        </div>
        <p className="br-file-upload__or-separator">or</p>
        <div className="br-file-upload__manual-address-container">
          <div className="br-file-upload__cta --manual-address">
            <img
              src={iconMap('locationPin')}
              className="br-file-upload__cta-image"
              alt="upload icon"
            />
            <p className="br-file-upload__cta-body">Enter your address</p>
            <Button
              className="br-file-upload__cta-button"
              content="Get started"
              onClick={handleManualAddressClicked}
            />
          </div>
        </div>
      </section>
    </>
  )
}

export default FileUpload
